/// <reference types="react-scripts" />
import { LockedRecord } from "ui/Scripts/source/models/common/lockedRecord";
import * as calendarTaskService from "../../models/services/calendarTaskService";
import * as React from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { Base } from "ui/Scripts/source/framework/base";
import { Dialog } from "../framework/dialog";
import { DialogButtonType, DatabaseRecordType } from "ui/Scripts/source/models/enums";
import { AppUtils } from "../../framework/appUtils";
import { CalendarTaskEditView, ICalendarTaskEditViewRefProps } from "./calendarTaskEditView";
import { IUserState } from "source/models/store/storeTypes";
import { ICalendarTaskEditItem } from "ui/Scripts/source/models/calendarTask/calendarTaskEditItem";
import { ICalendarTaskCategoryListItem } from "ui/Scripts/source/models/calendarTaskCategory/calendarTaskCategoryListItem";
import { useEffect, useRef, useState } from "react";
import { IModifyUser } from "ui/Scripts/source/models/user/modifyUser";

export interface ICalendarTaskEditDialogProps {
    user: IUserState;
    isReadOnly?: boolean;
    databaseRecordType: DatabaseRecordType;
    recordId: string;
    calendarTaskId: string;
    onRemove: () => void;
    onClose: () => void;
}

export interface ICalendarTaskEditDialogState {
    isLoading: boolean;
    userIsIsSystemAdmin: boolean;
    canSaveCalendarTask: boolean;
    canRemoveCalendarTask: boolean;
    canShareCalendarTasks: boolean;
    calendarTask: ICalendarTaskEditItem;
    categories: ICalendarTaskCategoryListItem[];
    lockedUser: IModifyUser;
    isLockedToOtherUser: boolean;
}

export const CalendarTaskEditDialog = (props: ICalendarTaskEditDialogProps) => {
    const editViewRef = useRef<ICalendarTaskEditViewRefProps>(null);
    const isNew = props.calendarTaskId === Base.emptyGuid;

    const [state, setState] = useState<ICalendarTaskEditDialogState>({
        isLoading: false,
        userIsIsSystemAdmin: false,
        canSaveCalendarTask: false,
        canRemoveCalendarTask: false,
        canShareCalendarTasks: false,
        calendarTask: null,
        categories: [],
        lockedUser: null,
        isLockedToOtherUser: false
    });

    const loadCalendarTask = async (id: string) => {
        //Unlock current task
        if (LockedRecord.isLockedToUser(editViewRef.current?.getLockedUser(), props.user.userCode)) {
            await AppUtils.callService(() => calendarTaskService.unlock(id, false));
        }
        //Load and lock task
        setState((old) => ({
            ...old,
            isLoading: true
        }));
        const result = await AppUtils.callService(() => calendarTaskService.getCalendarTaskEdit(id, props.recordId, props.databaseRecordType), false, false, () => setState((old) => ({ ...old, isLoading: false })));
        if (!result) return;
        const calendarTask = result.item;
        const newState = {
            ...state,
            userIsIsSystemAdmin: result.userIsIsSystemAdmin,
            canSaveCalendarTask: result.canSaveCalendarTask,
            canRemoveCalendarTask: result.canRemoveCalendarTask,
            canShareCalendarTasks: result.canShareCalendarTasks,
            calendarTask,
            categories: result.categories,
            lockedUser: calendarTask.lockedUser,
            isLockedToOtherUser: calendarTask.isLockedToOtherUser(props.user.userCode),
        };
        setState((old) => ({
            ...old,
            ...newState,
        }));
    };
    
    useEffect(() => {
        const loadData = async () => {
            await loadCalendarTask(props.calendarTaskId);
        };

        loadData();
    }, []);

    const handleClose = () => {
        editViewRef.current.close();
    };

    const handleSave = async () => {
        const result = await editViewRef.current.save();
        if (!result) return;
        props.onClose();
    };

    if (!state.calendarTask) return null;

    return (
        <Dialog
            classes="calendarTaskEditDialog"
            title={(state.calendarTask.parentId
                ? Translations.CalendarTaskOccurrence
                : (state.calendarTask.hasChildren
                    ? Translations.CalendarTaskSeries
                    : Translations.CalendarTask)) + " - " +
                        (isNew
                            ? Translations.ANew
                            : state.calendarTask.title)}
            body={
                <CalendarTaskEditView
                    ref={editViewRef}
                    inFullView={false}
                    isReadOnly={props.isReadOnly}
                    user={props.user}
                    canSaveCalendarTask={state.canSaveCalendarTask}
                    canRemoveCalendarTask={state.canRemoveCalendarTask}
                    canShareCalendarTasks={state.canShareCalendarTasks}
                    userIsIsSystemAdmin={state.userIsIsSystemAdmin}
                    calendarTaskId={props.calendarTaskId}
                    calendarTask={state.calendarTask}
                    categories={state.categories}
                    onClose={props.onClose}
                />
            }
            buttons={[
                { title: Translations.Save, buttonType: DialogButtonType.Primary, classes: (props.isReadOnly ? " d-none" : ""), disabled: props.isReadOnly, onClick: handleSave },
                { title: Translations.Remove, buttonType: DialogButtonType.Default, classes: (props.isReadOnly ? " d-none" : ""), disabled: props.isReadOnly, onClick: props.onRemove },
                { title: !props.isReadOnly ? Translations.Cancel : Translations.Close, buttonType: DialogButtonType.Default, onClick: handleClose }
            ]}
            onClose={handleClose}
        />
    );
};